import * as React from 'react';
import * as api from '../services/api-client';

/**
 * Settings state management for the Admin Console.
 * System_Admin only: view and update system configuration.
 * Implements optimistic updates with revert on failure.
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export interface GroupRoleMap {
  readonly systemAdmin: readonly string[];
  readonly domainAdmin: readonly string[];
  readonly user: readonly string[];
}

export interface ApiKeyPolicy {
  readonly maxLifetimeMs: number;
  readonly defaultLifetimeMs: number;
  readonly maxKeysPerSubdomain: number;
}

export interface SystemSettings {
  readonly groupRoleMap: GroupRoleMap;
  readonly blumiraEndpoint: string;
  readonly blumiraApiKeySecretArn: string;
  readonly apiKeyPolicy: ApiKeyPolicy;
  readonly updatedAt: string;
  readonly updatedBy: string;
}

export interface UpdateSettingsInput {
  readonly groupRoleMap: GroupRoleMap;
  readonly blumiraEndpoint: string;
  readonly blumiraApiKeySecretArn: string;
  readonly apiKeyPolicy: ApiKeyPolicy;
}

// ─── State ───────────────────────────────────────────────────────────────────

export interface SettingsState {
  readonly settings: SystemSettings | null;
  readonly isLoading: boolean;
  readonly isSaving: boolean;
  readonly error: string | null;
}

export const initialSettingsState: SettingsState = {
  settings: null,
  isLoading: false,
  isSaving: false,
  error: null,
};

// ─── Actions ─────────────────────────────────────────────────────────────────

type SettingsAction =
  | { readonly type: 'SETTINGS_LOADING' }
  | { readonly type: 'SETTINGS_LOADED'; readonly payload: SystemSettings | null }
  | { readonly type: 'SETTINGS_ERROR'; readonly payload: string }
  | { readonly type: 'SETTINGS_SAVING' }
  | { readonly type: 'SETTINGS_SAVE_OPTIMISTIC'; readonly payload: SystemSettings }
  | { readonly type: 'SETTINGS_SAVE_CONFIRMED' }
  | { readonly type: 'SETTINGS_SAVE_REVERT'; readonly payload: { readonly previousSettings: SystemSettings | null } };

// ─── Reducer ─────────────────────────────────────────────────────────────────

export function settingsReducer(state: SettingsState, action: SettingsAction): SettingsState {
  switch (action.type) {
    case 'SETTINGS_LOADING':
      return { ...state, isLoading: true, error: null };
    case 'SETTINGS_LOADED':
      return { settings: action.payload, isLoading: false, isSaving: false, error: null };
    case 'SETTINGS_ERROR':
      return { ...state, isLoading: false, isSaving: false, error: action.payload };
    case 'SETTINGS_SAVING':
      return { ...state, isSaving: true, error: null };
    case 'SETTINGS_SAVE_OPTIMISTIC':
      return { ...state, settings: action.payload, isSaving: true, error: null };
    case 'SETTINGS_SAVE_CONFIRMED':
      return { ...state, isSaving: false };
    case 'SETTINGS_SAVE_REVERT':
      return { ...state, settings: action.payload.previousSettings, isSaving: false, error: 'Failed to save settings. Changes reverted.' };
    default:
      return state;
  }
}

// ─── Context ─────────────────────────────────────────────────────────────────

interface SettingsContextValue {
  readonly state: SettingsState;
  readonly loadSettings: () => Promise<void>;
  readonly updateSettings: (input: UpdateSettingsInput) => Promise<void>;
}

const SettingsContext = React.createContext<SettingsContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────────────────────

interface SettingsProviderProps {
  readonly children: React.ReactNode;
}

/**
 * SettingsProvider manages system settings state with optimistic updates.
 * System_Admin only — authorization enforced at the page level.
 */
export function SettingsProvider({ children }: SettingsProviderProps): React.JSX.Element {
  const [state, dispatch] = React.useReducer(settingsReducer, initialSettingsState);
  const stateRef = React.useRef(state.settings);
  stateRef.current = state.settings;

  const loadSettings = React.useCallback(async (): Promise<void> => {
    dispatch({ type: 'SETTINGS_LOADING' });
    try {
      const data = await api.get<{ settings: SystemSettings | null }>('/admin/settings');
      dispatch({ type: 'SETTINGS_LOADED', payload: data.settings });
    } catch (err) {
      dispatch({
        type: 'SETTINGS_ERROR',
        payload: err instanceof Error ? err.message : 'Failed to load settings',
      });
    }
  }, []);

  const updateSettings = React.useCallback(async (input: UpdateSettingsInput): Promise<void> => {
    const previousSettings = stateRef.current;
    const optimistic: SystemSettings = {
      ...input,
      updatedAt: new Date().toISOString(),
      updatedBy: 'current-user',
    };

    dispatch({ type: 'SETTINGS_SAVE_OPTIMISTIC', payload: optimistic });
    try {
      const data = await api.put<{ settings: SystemSettings }>('/admin/settings', input);
      dispatch({ type: 'SETTINGS_SAVE_CONFIRMED' });
      // Update with server response for accurate timestamps
      dispatch({ type: 'SETTINGS_LOADED', payload: data.settings });
    } catch {
      dispatch({ type: 'SETTINGS_SAVE_REVERT', payload: { previousSettings } });
    }
  }, []);

  const contextValue = React.useMemo(
    (): SettingsContextValue => ({
      state,
      loadSettings,
      updateSettings,
    }),
    [state, loadSettings, updateSettings],
  );

  return React.createElement(SettingsContext.Provider, { value: contextValue }, children);
}

// ─── Consumer Hook ───────────────────────────────────────────────────────────

/**
 * Hook to access settings state and actions.
 * Must be used within a SettingsProvider.
 */
export function useSettings(): SettingsContextValue {
  const context = React.useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}
